import { Link } from "react-router-dom";
import { ArrowUpRight, Eye, Heart, MessageCircle } from "lucide-react";
import { Reveal, Kicker } from "@/components/Reveal";

const HIGHLIGHTS = [
  {
    src: "/videos/mr-kebab-ig.mp4", poster: "/posters/mr-kebab-ig.jpg", client: "MISTER KEBAB", tag: "INSTAGRAM REEL", to: "/progetti/mister-kebab",
    insights: [{ icon: Heart, value: "59K+" }, { icon: MessageCircle, value: "318" }],
  },
  {
    src: "/videos/drago-ramen-hero.mp4", poster: "/posters/drago-ramen-hero.jpg", client: "DRAGO RAMEN", tag: "TIKTOK", to: "/progetti/drago-ramen",
    insights: [{ icon: Eye, value: "238K+" }, { icon: Heart, value: "3.1K" }],
  },
  {
    src: "/videos/vb-beauty.mp4", poster: "/posters/vb-beauty.jpg", client: "VB BEAUTY LAB ACADEMY", tag: "TIKTOK", to: "/progetti/vb-beauty",
    insights: [{ icon: Eye, value: "31.8K" }, { icon: Heart, value: "328" }],
  },
];

const VideoCard = ({ v, i }) => (
  <Reveal delay={i * 0.1} className="h-full">
    <Link
      to={v.to}
      data-testid={`highlight-card-${i + 1}`}
      className={`group relative block aspect-[9/16] w-[70vw] shrink-0 snap-center overflow-hidden rounded-2xl bg-navy text-left shadow-[0_24px_60px_-30px_rgba(10,18,41,0.55)] transition-[transform,box-shadow] duration-500 hover:-translate-y-2 hover:shadow-[0_34px_70px_-28px_rgba(37,99,235,0.45)] sm:w-auto ${i === 1 ? "lg:-translate-y-6" : ""}`}
      aria-label={`Apri il progetto ${v.client}`}
    >
      <video
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
        src={v.src}
        poster={v.poster}
        muted
        loop
        playsInline
        autoPlay
        preload="metadata"
        aria-label={`Video del progetto ${v.client}`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/10 to-transparent" aria-hidden="true" />
      <span className="absolute left-3 top-3 rounded-full bg-white/10 px-3 py-1.5 font-mono text-[9px] font-semibold tracking-[0.2em] text-white backdrop-blur-md">
        {v.tag}
      </span>
      <div className="absolute inset-x-0 bottom-0 p-5">
        <div className="flex items-end justify-between gap-3">
          <h3 className="font-display text-lg font-black leading-tight tracking-tight text-white sm:text-xl">{v.client}</h3>
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition-all duration-300 group-hover:bg-brand-gradient">
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>
        <div className="mt-2.5 flex items-center gap-4" data-testid={`highlight-insights-${i + 1}`}>
          {v.insights.map((ins) => (
            <span key={ins.value} className="flex items-center gap-1.5 font-mono text-[11px] font-semibold tracking-[0.1em] text-white/85">
              <ins.icon className="h-3.5 w-3.5 text-brand-cyan" /> {ins.value}
            </span>
          ))}
        </div>
      </div>
    </Link>
  </Reveal>
);

export const ProjectsTeaser = () => (
  <section data-testid="projects-teaser" className="bg-white py-20 lg:py-28">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <Kicker>Progetti in evidenza</Kicker>
        <div className="mt-5 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <h2 className="max-w-2xl font-display text-4xl font-black leading-[0.98] tracking-tight text-navy sm:text-6xl">
            I PROGETTI <span className="text-brand-gradient">PARLANO PER NOI.</span>
          </h2>
          <p className="max-w-md text-base leading-relaxed text-slate-600">
            Non creiamo contenuti per riempire un calendario. Costruiamo idee che catturano l'attenzione, rendono riconoscibili i brand e generano opportunità.
          </p>
        </div>
      </Reveal>

      <div className="mt-14 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4 sm:grid sm:grid-cols-3 sm:overflow-visible sm:pb-0 lg:gap-8" data-testid="highlights-row">
        {HIGHLIGHTS.map((v, i) => (
          <VideoCard key={v.src} v={v} i={i} />
        ))}
      </div>

      <Reveal delay={0.15}>
        <div className="mt-12 text-center">
          <Link
            to="/progetti"
            data-testid="teaser-cta-projects"
            className="group inline-flex items-center gap-3 font-display text-sm font-bold tracking-[0.15em] text-navy transition-colors hover:text-brand-blue"
          >
            SCOPRI TUTTI I PROGETTI
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-gradient text-white transition-transform duration-300 group-hover:translate-x-1">
              <ArrowUpRight className="h-4 w-4" />
            </span>
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);
